import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import Section from '../components/ui/Section';
import SectionHeader from '../components/ui/SectionHeader';

const Solutions = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const solutions = [
    {
      icon: '/icons/strategy.svg',
      title: t('solutions.items.strategy.title'),
      description: t('solutions.items.strategy.description'),
      bullets: t('solutions.items.strategy.bullets', { returnObjects: true }),
    },
    {
      icon: '/icons/engineering.svg',
      title: t('solutions.items.engineering.title'),
      description: t('solutions.items.engineering.description'),
      bullets: t('solutions.items.engineering.bullets', { returnObjects: true }),
    },
    {
      icon: '/icons/ai.svg',
      title: t('solutions.items.ai.title'),
      description: t('solutions.items.ai.description'),
      bullets: t('solutions.items.ai.bullets', { returnObjects: true }),
    },
    {
      icon: '/icons/cloud.svg',
      title: t('solutions.items.cloud.title'),
      description: t('solutions.items.cloud.description'),
      bullets: t('solutions.items.cloud.bullets', { returnObjects: true }),
    },
    {
      icon: '/icons/design.svg',
      title: t('solutions.items.design.title'),
      description: t('solutions.items.design.description'),
      bullets: t('solutions.items.design.bullets', { returnObjects: true }),
    },
    {
      icon: '/icons/integration.svg',
      title: t('solutions.items.integration.title'),
      description: t('solutions.items.integration.description'),
      bullets: t('solutions.items.integration.bullets', { returnObjects: true }),
    },
  ];

  return (
    <Section id="solutions" withFlyingElements={true}>
      <div className="text-center mb-4">
        <motion.button
          initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
          whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className={`px-4 py-2 ${isDark ? 'bg-white/5' : 'bg-afsc-charcoal/5'} backdrop-blur-sm border ${isDark ? 'border-white/10' : 'border-afsc-charcoal/10'} rounded-lg ${isDark ? 'text-white' : 'text-afsc-charcoal'} text-sm font-medium mb-8 inline-flex items-center gap-2 hover:border-afsc-gold transition-colors`}
        >
          <span className="text-afsc-gold">→</span>
          {t('solutions.badge')}
        </motion.button>
      </div>
      
      <SectionHeader
        title={t('solutions.title')}
        subtitle={t('solutions.subtitle')}
        withDivider={true}
      />
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {solutions.map((solution, index) => (
          <motion.div
            key={solution.title}
            initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1, duration: 0.6 }}
            className="group"
          >
            <div className={`h-full ${isDark ? 'bg-afsc-slate/80' : 'bg-white/60'} backdrop-blur-sm rounded-2xl p-8 border border-afsc-gold/30 transition-all duration-300 hover:border-afsc-gold/60 hover:shadow-[0_0_20px_rgba(203,163,90,0.3)] hover:shadow-afsc-gold/30`}>
              {/* Icon Circle */}
              <div className={`w-16 h-16 rounded-full ${isDark ? 'bg-afsc-gold/20' : 'bg-afsc-gold/15'} flex items-center justify-center mb-6 group-hover:bg-afsc-gold/30 transition-colors`}>
                <img 
                  src={solution.icon} 
                  alt={solution.title}
                  className="w-8 h-8 object-contain"
                />
              </div>
              
              {/* Title */}
              <h3 className={`text-2xl md:text-3xl font-heading font-bold mb-4 ${isDark ? 'text-white' : 'text-afsc-charcoal'}`}>
                {solution.title}
              </h3>
              
              {/* Description */}
              <p className={`mb-6 leading-relaxed ${isDark ? 'text-white/80' : 'text-afsc-charcoal/70'}`}>
                {solution.description}
              </p>
              
              {/* Bullets */}
              <ul className="space-y-3">
                {solution.bullets.map((bullet, i) => (
                  <li key={i} className={`flex items-start text-sm ${isDark ? 'text-white/70' : 'text-afsc-charcoal/70'}`}>
                    <span className="w-5 h-5 rounded-full bg-afsc-gold flex items-center justify-center mx-3 mt-0.5 flex-shrink-0">
                      <span className={`text-xs ${isDark ? 'text-white' : 'text-afsc-charcoal'}`}>✓</span>
                    </span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
};

export default Solutions;

